import { useEffect } from "react";
import { useBlocker } from "react-router";

const DEFAULT_MESSAGE =
  "Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je deze pagina wilt verlaten?";

/**
 * Asks for confirmation before leaving the page while there are unsaved changes. Covers in-app
 * navigation (links, back button) as well as closing/reloading the tab.
 *
 * In-app blocking relies on `useBlocker`, which requires a data router (see `main.tsx`).
 */
export const useUnsavedChangesPrompt = (dirty: boolean, message: string = DEFAULT_MESSAGE) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(message)) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, message]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by some browsers to trigger the native "leave site?" dialog.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
};
